import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import dayjs from 'dayjs';
import { TIME_REGEX, SpotifyReportSchedule } from 'wb-spotify/app/common/utils';
import { useCallback } from 'react';
import useCreateSpotifyReport from 'wb-spotify/app/routes/reports/create/useCreateSpotifyReport';
import { getDatesFromSchedule, getReportClientIds, ReportRow } from 'wb-spotify/app/routes/reports/utils';
import { useTranslation } from 'react-i18next';
import { LIFE_TIME_METRICS } from 'wb-spotify/app/routes/superGrid/statistics/utils';

function useSetCreateSpotifyReportData() {
	const { t } = useTranslation();

	const {
		setFilters, setBreakdowns, setMetrics, setDates,
		setSchedule, setUserGroupIds, setTime, setPrivateUserGroupIds,
		setReportId, setName,
	} = useCreateSpotifyReport();

	const { toggleLifetimeStats } = useSupergridSettings();

	return useCallback(
		(report: ReportRow) => {
			const clientIds = getReportClientIds(report?.reportFiltersOptions);
			const key = 'userGroupId';

			// @ts-ignore TODO might not work if metric is not in the list
			const isLifetimeStats = LIFE_TIME_METRICS.some(metric => report.reportMetrics?.includes?.(metric))
			// @ts-ignore
			|| report.reportDimensions?.every(breakdown => breakdown !== 'date');

			if (isLifetimeStats) {
				toggleLifetimeStats();
			}

			if (clientIds.length > 0 && report?.reportId) {
				setReportId(report?.reportId);
				setFilters({
					[key]: [{ value: clientIds }],
				});
				setName(report.reportName ?? t('re: reports.editing', 'Editing report'));

				// @ts-ignore
				setBreakdowns((report.reportDimensions));

				// @ts-ignore
				setMetrics(report.reportMetrics);

				setUserGroupIds(clientIds ?? []);
				// @ts-ignore
				setPrivateUserGroupIds(report.userGroupId ?? []);

				// TODO: Check if we need to set user ids
				// setUserIds(report.users?.map(user => user?.id ?? '') ?? []);

				const dateRangeValues = report.reportDateRangeOptions?.dateRangeValue ?? [];

				if (dateRangeValues?.length > 0) {
					setDates({ from: dayjs(dateRangeValues?.[0]), to: dayjs(dateRangeValues?.[1]) });
				} else {
					setDates(
						getDatesFromSchedule({ selectedSchedule: report.reportSchedule as SpotifyReportSchedule }),
					);
				}

				// Keep typecast since in schema it is string, but in reality it is enum
				setSchedule(report.reportSchedule as SpotifyReportSchedule);

				if (TIME_REGEX.test(report.reportSchedule)) {
					setSchedule(SpotifyReportSchedule.OnlyOnce);
					setTime(report.reportSchedule);
				}
			}
		},
		[
			setFilters, setBreakdowns, setMetrics, setDates,
			setSchedule, setUserGroupIds, setTime, setPrivateUserGroupIds,
			setReportId, setName, toggleLifetimeStats,
		],
	);
}

export default useSetCreateSpotifyReportData;
